import clonedeep from 'lodash.clonedeep';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { PageId, WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import {
  blendMultipleRgbaColors,
  generateComplimentaryLightColor,
  generatePaletteFromColor,
  getAlpha,
  isGrayScale,
  isNeutral,
  isTransparent,
  maintainAlpha,
  toHex,
} from 'utils/color';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { isHighChartsWidgetType } from 'widgets/ChartWidgets/ChartWidget.types';
import { isMonoIcon } from 'widgets/IconWidget/IconWidget.helpers';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { getOverlayTransparency, hasOverlay } from 'widgets/ImageWidget/ImageWidget.helpers';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { measureTextWidgetHeight } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { GET_WIDGET_COLOR_FN_MAP, SET_WIDGET_COLOR_FN_MAP } from 'widgets/Widget.config';
import { getFlattenedLayerOrder, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, WidgetMap } from 'widgets/Widget.types';
import { getWidgetRect, intersectRect, isWidgetContained, isWidgetWrapping } from 'widgets/WidgetLayout.helpers';

import {
  collapseColors,
  createLayoutMap,
  generateDataColors,
  getBackgroundColor,
  getBaseLayoutColorsSorted,
  getContrastTextColor,
  getInitialColor,
  getStatchartParentWidgetRect,
  isSameAsPageBackground,
  randomPickThemeColor,
  replaceGradientColors,
  setInitialPageColors,
  setInitialWidgetColors,
  shouldSkipBorderColor,
  shuffleThemeColors,
} from './colorShuffle.helpers';
import {
  BaseLayoutWidgets,
  ColorMap,
  ColorMapArr,
  ColorMapConfig,
  DecorativeLayoutWidgetMap,
  FullyCoveredLayoutWidgets,
  InitialPageColors,
  InitialWidgetColors,
  LayoutMap,
  PageGradientColorMap,
  PartiallyCoveredLayoutWidgets,
  TableColor,
  TableColorMap,
  Theme,
  UpdatePageColor,
  WidgetColors,
} from './colorShuffle.types';

export class ColorShuffle {
  // the minimum text color contrast ratio after shuffling - text will be updated to match this ratio
  private MIN_TEXT_COLOR_CONTRAST = 4.5;
  // if the background color is light we keep it light - this is the luminance threshold
  private BACKGROUND_COLOR_LUMINANCE = 0.9;
  // skip widgets with a rgba alpha below this threshold (e.g. won't be used for contrast checks)
  private ALPHA_THRESHOLD = 0.1;

  // required for checking the color of icons to see if they are mono colored or not
  private iconDataCache: Map<string, any>;

  // keep the initial colors in case in case else colors won't be persisted when shuffling from e.g. 4 - 2 - 4 colors
  private initialWidgetColors: InitialWidgetColors = {};
  private initialPageColors: InitialPageColors = {};

  // we use these in multiple methods so we store it here so we don't have to pass it around so much
  private widgets: WidgetMap = {};

  // [old color] - [new color]
  private colorMap: ColorMap = {};
  private pageColorMap: ColorMap = {};
  private pageGradientColorMap: PageGradientColorMap = {};

  // [widgetId] - [new color]
  private widgetColors: WidgetColors = {};
  // new data colors for charts, statcharts
  // [widgetId] - [new data colors]
  private dataColors: ColorMapArr = {};
  // [widgetId] - [new border color]
  private borderColors: ColorMap = {};
  // new table cell + text colors
  private tableColors: TableColorMap = {};
  private updateWidgets: UpdateWidget[] = [];
  private updatePageColors: UpdatePageColor = {};
  private theme: Theme = [];
  // extra theme colors - can be used for nested widgets / charts
  private extraThemeColors: Theme = [];

  // store the previous theme - so we don't shuffle the same order of colors
  private previousTheme: Theme = [];

  constructor(iconDataCache: Map<string, any>) {
    this.iconDataCache = iconDataCache;
  }

  /**
   * Retrieves the decorative layout widgets from the flattened layer order.
   * Decorative layout widgets are e.g. single shapes / icons without any text or other icons on top of them.
   *
   * @param flattenedLayerOrder - The flattened layer order of the widgets.
   * @returns An array of decorative widgets with their respective IDs and layer positions.
   */
  private getDecorativeLayoutWidgets = (flattenedLayerOrder: WidgetId[]) => {
    const decorativeWidgets = [];

    for (let i = 0; i < flattenedLayerOrder.length; i++) {
      const widgetId = flattenedLayerOrder[i];
      const widget = this.widgets[widgetId];
      const widgetLayerPosition = i;

      if (!this.isLayoutWidget(widgetId)) continue;

      // iterate over all other widgets and find which shapes it intersects with
      let intersectIds = [];

      for (let j = 0; j < flattenedLayerOrder.length; j++) {
        const otherWidgetId = flattenedLayerOrder[j];
        const otherWidgetType = getWidgetTypeFromId(otherWidgetId);
        const otherWidgetLayerPosition = j;
        const otherWidget = this.widgets[otherWidgetId];
        const isOtherBasicShape = otherWidgetType === WidgetType.BasicShape;

        if (widgetId === otherWidgetId) continue;

        // can only intersect with widgets that are above it
        if (widgetLayerPosition > otherWidgetLayerPosition) continue;

        // if its transparent skip it - most likely a border
        if (isOtherBasicShape && isTransparent((otherWidget as BasicShapeWidgetData)?.fillColor[0])) continue;

        // check if the widget is intersected by another widget - if so
        const isIntersected = intersectRect(widget, otherWidget, 2);
        if (isIntersected) intersectIds.push({ id: otherWidgetId });
      }

      // if its fully covered by another widget below than use that as background
      if (intersectIds.length > 0) {
        let isDecorative = true;

        intersectIds.forEach((intersect) => {
          const type = getWidgetTypeFromId(intersect.id);

          // If a widget has these widgets on top of it it is not considered as decorative - and it will be used as a layoutwidget
          if (type === WidgetType.Text) isDecorative = false;
          if (type === WidgetType.Table) isDecorative = false;
          if (type === WidgetType.Icon) isDecorative = false;
        });

        if (isDecorative) decorativeWidgets.push({ id: widgetId, layerPosition: widgetLayerPosition });
      } else {
        decorativeWidgets.push({ id: widgetId, layerPosition: widgetLayerPosition });
      }
    }

    return decorativeWidgets;
  };

  /**
   * Checks if a widget is considered as a layout widget (used for the basis of the color shuffle algorithm).
   *
   * @param widgetId - The ID of the widget.
   * @returns True if the widget is a layout widget, false otherwise.
   */
  private isLayoutWidget = (widgetId: WidgetId) => {
    const widgetType = getWidgetTypeFromId(widgetId);
    const isBasicShape = widgetType === WidgetType.BasicShape;

    return isBasicShape || isMonoIcon(widgetType, this.widgets[widgetId] as IconWidgetData, this.iconDataCache);
  };

  /**
   * Retrieves the base layout widgets for the page from the layer order
   * A layout widget is considered as a base layout widget if its directly on top of the background
   *
   * @param flattenedLayerOrder - The flattened layer order of the widgets.
   * @returns An array of base layout widgets, each containing the widget ID and its layer position.
   */
  private getPageBaseLayoutWidgets = (flattenedLayerOrder: WidgetId[]) => {
    const baseLayoutWidgets = [];

    for (let i = 0; i < flattenedLayerOrder.length; i++) {
      const widgetId = flattenedLayerOrder[i];
      const widget = this.widgets[widgetId];
      const widgetLayerPosition = i;

      // If the widget is not considered as a layout widget, skip it
      if (!this.isLayoutWidget(widgetId)) continue;

      // iterate over all other widgets to check if it intersects with any other widget
      let intersects = false;

      for (let j = 0; j < flattenedLayerOrder.length; j++) {
        const otherWidgetId = flattenedLayerOrder[j];
        const otherWidgetType = getWidgetTypeFromId(otherWidgetId);
        const otherWidget = this.widgets[otherWidgetId];
        const otherWidgetLayerPosition = j;
        const isOtherBasicShape = otherWidgetType === WidgetType.BasicShape;

        if (!this.isLayoutWidget(otherWidgetId)) continue;
        if (widgetId === otherWidgetId) continue;

        // skip transparent
        if (isOtherBasicShape && isTransparent((otherWidget as BasicShapeWidgetData)?.fillColor[0])) continue;

        // can only intersect with widgets that are below it
        if (otherWidgetLayerPosition > widgetLayerPosition) continue;

        // it is intersected by another widget we can stop checking
        const isIntersected = isWidgetContained(otherWidget, widget, 2);
        if (isIntersected) {
          // if the widget is larger and on top of the other widget it covers it entirely and we can skip it as it doesn't intersect
          const fullyCovers = isWidgetWrapping(getWidgetRect(widget), getWidgetRect(otherWidget));
          if (fullyCovers) continue;

          intersects = true;
          break;
        }
      }

      // if it doesn't intersect with any other widget it is a base layout widget
      if (!intersects) baseLayoutWidgets.push({ id: widgetId, layerPosition: widgetLayerPosition });
    }

    return baseLayoutWidgets;
  };

  /**
   * Returns the ID of a layout widget that fully covers the specified widget.
   * (If such a widget exists)
   *
   * @param flattenedLayerOrder - The order of the widgets in the layer.
   * @param layerPosition - The position of the widget in the layer.
   * @param widgetId - The ID of the widget to check.
   * @returns widgetId | undefined
   */
  private getFullyCoveredLayoutWidget = (
    flattenedLayerOrder: WidgetId[],
    layerPosition: number,
    widgetId: WidgetId,
  ) => {
    let containedByWidgetId;
    const widgetRect = getWidgetRect(this.widgets[widgetId]);

    // iterate backwards since a fully covered shape must be behind the current shape
    for (let j = layerPosition; j >= 0; j--) {
      const otherWidgetId = flattenedLayerOrder[j];
      if (!otherWidgetId) continue;
      if (widgetId === otherWidgetId) continue;

      const otherWidgetType = getWidgetTypeFromId(otherWidgetId);
      const otherWidget = this.widgets[otherWidgetId];

      const isOtherBasicShape = otherWidgetType === WidgetType.BasicShape;
      if (!this.isLayoutWidget(otherWidgetId)) continue;

      // if its transparent skip it - most likely a border
      if (isOtherBasicShape && isTransparent((otherWidget as BasicShapeWidgetData)?.fillColor[0])) continue;

      // it is intersected by another widget we can stop checking
      const otherWidgetRect = getWidgetRect(otherWidget);
      // if up to 95% of the widget is covered by another widget it is considered fully covered
      const isContaining = isWidgetContained(otherWidgetRect, widgetRect, 95);

      if (isContaining) {
        containedByWidgetId = otherWidgetId;
        break;
      }
    }

    return containedByWidgetId;
  };

  /**
   * Finds the fully covered layout widgets on the page from the layer order
   *
   * @param flattenedLayerOrder - The flattened order of the widgets on the page.
   */
  private getPageFullyCoveredLayoutWidgets = (flattenedLayerOrder: WidgetId[]) => {
    const fullyCoveredLayouWidgets = [];

    for (let i = 0; i < flattenedLayerOrder.length; i++) {
      const widgetId = flattenedLayerOrder[i];
      if (!this.isLayoutWidget(widgetId)) continue;

      // iterate over all other widgets to check if it covered with any other widget
      const wrappedById = this.getFullyCoveredLayoutWidget(flattenedLayerOrder, i, widgetId);

      for (let j = 0; j < flattenedLayerOrder.length; j++) {
        const otherWidgetId = flattenedLayerOrder[j];
        if (!this.isLayoutWidget(otherWidgetId)) continue;
        if (widgetId === otherWidgetId) continue;
      }

      if (wrappedById) fullyCoveredLayouWidgets.push({ id: widgetId, layerPosition: i, wrappedById });
    }

    return fullyCoveredLayouWidgets;
  };

  /**
   * Finds the partially covered layout widgets on the page from the layer order (e.g. partially overlapped by one or more layout widgets)
   *
   * @param flattenedLayerOrder - The flattened order of the widgets on the page.
   * @param decorativeLayoutWidgetMap - A map of the decorative layout widgets.
   */
  private getPagePartiallyCoveredLayoutWidgets = (
    flattenedLayerOrder: WidgetId[],
    decorativeLayoutWidgetMap: DecorativeLayoutWidgetMap,
  ) => {
    const partiallyCoveredLayoutWidgets = [];

    for (let i = 0; i < flattenedLayerOrder.length; i++) {
      const widgetId = flattenedLayerOrder[i];
      const widget = this.widgets[widgetId];
      const widgetLayerPosition = i;

      if (!this.isLayoutWidget(widgetId)) continue;

      // iterate over all other widgets and find which shapes it intersects with
      let intersectIds = [];

      for (let j = 0; j < flattenedLayerOrder.length; j++) {
        const otherWidgetId = flattenedLayerOrder[j];
        if (widgetId === otherWidgetId) continue;

        const otherWidgetType = getWidgetTypeFromId(otherWidgetId);
        const otherWidget = this.widgets[otherWidgetId];

        const isOtherBasicShape = otherWidgetType === WidgetType.BasicShape;
        if (!this.isLayoutWidget(otherWidgetId)) continue;
        // if its transparent skip it - most likely a border
        if (isOtherBasicShape && isTransparent((otherWidget as BasicShapeWidgetData)?.fillColor[0])) continue;

        // it is intersected by another widget we can stop checking
        const isIntersected = intersectRect(widget, otherWidget);
        if (isIntersected) {
          // if the widget is larger and on top of the other widget it covers it entirely and we can skip it as it doesn't intersect
          const fullyCovers = isWidgetWrapping(getWidgetRect(widget), getWidgetRect(otherWidget));
          if (fullyCovers) continue;

          // skip fully covered widgets as well
          const fullyCovered = isWidgetWrapping(getWidgetRect(otherWidget), getWidgetRect(widget));
          if (fullyCovered) continue;
          if (decorativeLayoutWidgetMap[otherWidgetId]) continue;

          intersectIds.push({ id: otherWidgetId, position: 'intersected' });
        }
      }

      // if its fully covered by another widget below than use that as background - else page background?
      if (intersectIds.length > 0) {
        const fullyCoveredById = this.getFullyCoveredLayoutWidget(flattenedLayerOrder, i, widgetId);
        const background = fullyCoveredById ? fullyCoveredById : 'page';

        partiallyCoveredLayoutWidgets.push({
          id: widgetId,
          intersectIds,
          background,
          layerPosition: widgetLayerPosition,
        });
      }
    }

    return partiallyCoveredLayoutWidgets;
  };

  /**
   * Set the colors from the theme for the base layout widgets on the page
   *
   * @param baseLayoutWidgets - The base layout widgets.
   * @param initialPageColor - The initial page color.
   */
  private setPageBaseWidgetColors = (
    baseLayoutWidgets: BaseLayoutWidgets,
    initialPageColor: string,
    newPageColor: string,
    shouldKeepExistingColorMap: boolean,
  ) => {
    let baseColorArr = getBaseLayoutColorsSorted(baseLayoutWidgets, this.initialWidgetColors).filter(
      (color) => color !== initialPageColor,
    );
    const themeFiltered = this.theme.filter((color) => color !== newPageColor);
    collapseColors(themeFiltered, baseColorArr, this.colorMap, shouldKeepExistingColorMap);

    for (const shape of baseLayoutWidgets) {
      const initialColor = getInitialColor(this.initialWidgetColors[shape.id]);

      // if the color is the same as the page color - keep it the same
      if (
        isSameAsPageBackground(initialPageColor, getWidgetTypeFromId(shape.id), this.widgets[shape.id] as AllWidgetData)
      ) {
        this.widgetColors[shape.id] = newPageColor;
        continue;
      }

      const newColor = this.colorMap[initialColor];
      if (newColor) this.widgetColors[shape.id] = newColor;
    }
  };

  /**
   * Set the colors from the theme for the rest of the layout widgets on the page
   *
   * @param fullyCoveredLayoutWidgets - The fully covered layout widgets.
   * @param baseLayoutWidgets - The base layout widgets.
   */
  private setPageLayeredColors = (
    flattenedLayerOrder: WidgetId[],
    fullyCoveredLayoutWidgets: FullyCoveredLayoutWidgets,
    partiallyCoveredLayoutWidgets: PartiallyCoveredLayoutWidgets,
    layoutMap: LayoutMap,
    newPageColor: string,
    seed?: number,
  ) => {
    let pendingIds = [
      ...fullyCoveredLayoutWidgets.map((shape) => shape.id),
      ...partiallyCoveredLayoutWidgets.map((shape) => shape.id),
    ];

    let isStuck = false;
    // extra fallback here to prevent an infinite loop
    const maxIterations = 1000;
    let iterations = 0;

    // The colors used for overlapping widgets are not necessarily in order
    // -> use a while loop and iterate over the layer order to find the correct color for each widget
    while (pendingIds.length) {
      // extra fallback here to prevent an infinite loop
      iterations++;
      if (iterations > maxIterations) {
        console.warn('Max iterations reached');
        break;
      }

      // iterate over the flattened layer order and apply colors to the rest of the layout widgets
      for (let i = 0; i < flattenedLayerOrder.length; i++) {
        const widgetId = flattenedLayerOrder[i];
        if (this.widgetColors[widgetId]) continue;
        if (!this.isLayoutWidget(widgetId)) continue;

        const fullyCoveredWidget = fullyCoveredLayoutWidgets.find((shape) => shape.id === widgetId);
        const partiallyCoveredWidget = partiallyCoveredLayoutWidgets.find((shape) => shape.id === widgetId);
        const initialColor = getInitialColor(this.initialWidgetColors[widgetId]);

        // this color has already been assigned
        const assignedColor = this.colorMap[initialColor];

        if (fullyCoveredWidget && !partiallyCoveredWidget) {
          // pick a random color from the theme that is not the background
          const wrappedById = fullyCoveredWidget.wrappedById;
          let parentColor = this.widgetColors[wrappedById];
          const nestedKey = parentColor + '-' + initialColor;

          const assignedColorNested = this.colorMap[nestedKey];

          if (!parentColor) {
            if (isStuck) {
              // if for some reason the parent widget does not have a color - set it to the background
              console.warn('missing parent background for: ', wrappedById);
              parentColor = newPageColor;
              isStuck = false;
            } else {
              continue;
            }
          }

          const newColor = this.getlayeredShapeColor({
            widgetColor: assignedColorNested || assignedColor,
            initialColor,
            backgroundColors: [parentColor],
            initialBackgroundColor: getInitialColor(this.initialWidgetColors[wrappedById]),
            seed,
          });

          this.widgetColors[widgetId] = newColor;
          if (!assignedColor) this.colorMap[initialColor] = newColor;
          if (!this.colorMap[nestedKey]) this.colorMap[nestedKey] = newColor;

          continue;
        }

        if (partiallyCoveredWidget) {
          const { intersectIds } = partiallyCoveredWidget;

          let isSameColorAsIntersected = '';
          let sameColorIntersectId = '';

          const intersectedColors: string[] = [];
          let isMissingSomeColors = false;

          intersectIds.forEach((intersect) => {
            const intersectId = intersect.id;
            const intersectColor = this.widgetColors[intersectId];
            const intersectOriginalColor = getInitialColor(this.initialWidgetColors[intersectId]);

            if (intersectOriginalColor === initialColor && intersectColor) {
              isSameColorAsIntersected = intersectColor;
              sameColorIntersectId = intersectId;
            }

            if (!intersectColor) {
              const intersectLayerPosition = layoutMap[intersectId].layerPosition;
              if (intersectLayerPosition < partiallyCoveredWidget.layerPosition) {
                isMissingSomeColors = true;
              }
            } else {
              intersectedColors.push(intersectColor);
            }
          });

          if (isSameColorAsIntersected) {
            const intersectLayout = layoutMap[sameColorIntersectId];

            if (intersectLayout.type === 'non-intersected') {
              // set the color of the non-intersected one IF it is not the same as the background
              const key = intersectedColors.join('-') + '-' + initialColor;
              const assignedColorNested = this.colorMap[key];

              const newColor = this.getlayeredShapeColor({
                widgetColor: assignedColorNested || assignedColor,
                initialColor,
                backgroundColors: intersectedColors,
                backgroundColor: newPageColor,
                seed,
              });

              this.widgetColors[widgetId] = newColor;
              this.widgetColors[sameColorIntersectId] = newColor;
              if (!assignedColor) this.colorMap[initialColor] = newColor;
              if (!this.colorMap[key]) this.colorMap[key] = newColor;
              continue;
            }

            this.widgetColors[widgetId] = isSameColorAsIntersected;
            const key = intersectedColors.join('-') + '-' + initialColor;
            if (!this.colorMap[key]) this.colorMap[key] = isSameColorAsIntersected;
            continue;
          }

          if (isMissingSomeColors && !isStuck) {
            console.warn(`isMissingSomeIntersectedColors `, partiallyCoveredWidget);
            // this should only be for widgets on lower layers
            continue;
          }
          isStuck = false;

          const backgroundColor =
            partiallyCoveredWidget.background === 'page'
              ? newPageColor
              : this.widgetColors[partiallyCoveredWidget.background];

          const key = intersectedColors.join('-') + '-' + initialColor;
          const assignedColorNested = this.colorMap[key];

          const newColor = this.getlayeredShapeColor({
            widgetColor: assignedColorNested || assignedColor,
            initialColor,
            backgroundColors: intersectedColors,
            backgroundColor,
            seed,
          });

          this.widgetColors[widgetId] = newColor;
          if (!assignedColor) this.colorMap[initialColor] = newColor;
          if (!this.colorMap[key]) this.colorMap[key] = newColor;
          continue;
        }
      }

      const newPendingIds = pendingIds.filter((id) => !this.widgetColors[id]);
      if (newPendingIds.length === pendingIds.length) isStuck = true;

      pendingIds = newPendingIds;
    }
  };

  /**
   * Retrieves the layered shape color - which can vary based on the background colors - background color - and initial colors.
   *
   * @param widgetColor - The color of the widget.
   * @param initialColor - The initial color of the widget.
   * @param backgroundColors - An array of background colors.
   * @param backgroundColor - The background color.
   * @param initialBackgroundColor - The initial background color.
   *
   * @returns The suggested color for the layered shape.
   */
  private getlayeredShapeColor = ({
    widgetColor,
    initialColor,
    backgroundColors,
    backgroundColor,
    initialBackgroundColor,
    seed,
  }: {
    widgetColor: string;
    initialColor: string;
    backgroundColors: string[];
    initialBackgroundColor?: string;
    backgroundColor?: string;
    seed?: number;
  }) => {
    if (backgroundColors.length === 1 && initialColor === initialBackgroundColor) return backgroundColors[0];

    // if its white already - leave it as white
    if (isGrayScale(initialColor)) return initialColor;

    let filteredTheme = this.theme.filter((color) => !backgroundColors.includes(color));
    if (backgroundColor) filteredTheme = filteredTheme.filter((color) => color !== backgroundColor);

    // return the suggested color if it is not the background color
    const isSameAsBackground = backgroundColors.includes(widgetColor);
    if (widgetColor && !isSameAsBackground && backgroundColor !== widgetColor) {
      return widgetColor;
    }

    // check if there are available theme colors that can be used
    if (filteredTheme.length) return randomPickThemeColor(filteredTheme, this.colorMap, seed);

    // check if we can use white
    if (!backgroundColors.includes('#ffffff') && !filteredTheme.includes('#ffffff')) return '#ffffff';

    // if not pick a random color from the extra theme colors
    return randomPickThemeColor(this.extraThemeColors, this.colorMap, seed);
  };

  /**
   * Set the colors from the theme for all the rest of the widgets on the page (if the widget has colors to be set)
   *
   * @param flattenedLayerOrder - The flattened layer order of the widgets.
   * @param initialPageColor - The initial page color.
   * @param layerOrder - The unflattened layer order of the widgets (with groups/statcharts)
   */
  private setRemainingPageWidgetColors = (
    flattenedLayerOrder: WidgetId[],
    initialPageColor: string,
    layerOrder: WidgetId[],
    newPageColor: string,
    seed?: number,
  ) => {
    // stores the real height of text widgets (some old text widgets can have a height of 0 in this case we need to measure the height)
    const measuredTextWidgetHeightMap: { [key: WidgetId]: number } = {};

    for (let i = 0; i < flattenedLayerOrder.length; i++) {
      const widgetId = flattenedLayerOrder[i];
      const widgetType = getWidgetTypeFromId(widgetId);
      const isBasicShape = widgetType === WidgetType.BasicShape;
      const isBasicShapeWithBorder = isBasicShape && this.initialWidgetColors[widgetId]?.length > 1;

      if (this.widgetColors[widgetId] && !isBasicShapeWithBorder) {
        continue;
      }

      const initialColor = this.initialWidgetColors[widgetId];
      const isTextWidget = widgetType === WidgetType.Text;
      const isTableWidget = widgetType === WidgetType.Table;
      const isProgressChartWidget = widgetType === WidgetType.ProgressChart;
      const isLabelTextWidget = widgetType === WidgetType.LabelText;
      const isStatchartGridIcon =
        widgetType === WidgetType.Icon && (this.widgets[widgetId] as IconWidgetData)?.type === 'Grid';
      const isHighchartChartWidget = isHighChartsWidgetType(widgetType);
      const isScatterBubbleChartWidget = widgetType === WidgetType.ScatterBubbleChart;

      const isStatchart = isLabelTextWidget || isStatchartGridIcon || isProgressChartWidget;
      // if it has any text that should contrast (tables handled separately)
      const isWidgetWithText = isTextWidget || isHighchartChartWidget || isStatchart;

      // find the widget rect
      const widgetRect = isStatchart
        ? getStatchartParentWidgetRect(layerOrder, this.widgets, widgetId)
        : getWidgetRect(this.widgets[widgetId]);

      // some old text widgets can have a height of 0 in this case we need to measure the height
      // so that we can calculate if its on top of another widget correctly below
      const isOldTextWidgetWithZeroHeight = isTextWidget && widgetRect.heightPx === 0;
      if (isOldTextWidgetWithZeroHeight) {
        if (!measuredTextWidgetHeightMap[widgetId]) {
          measuredTextWidgetHeightMap[widgetId] = measureTextWidgetHeight(this.widgets[widgetId] as TextWidgetData);
        }
        widgetRect.heightPx = measuredTextWidgetHeightMap[widgetId];
      }

      // the background color of the page or widget behind
      let backgroundColor = newPageColor;
      let backgroundInitialColor = initialPageColor;

      // optional alpha of widget behind - a widget with alpha will need the widget behind as well for the contrast check
      let alphaColorArr = [];

      // if the color is the same as the background color - keep it the same
      let sameAsRectColor = '';

      // find the first widget that intersects with the current widget
      // iterate backwards since we only want the widgets that are behind the current widget
      for (let j = i; j >= 0; j--) {
        const otherWidgetId = flattenedLayerOrder[j];
        if (!otherWidgetId) continue;
        if (widgetId === otherWidgetId) continue;

        const otherWidgetType = getWidgetTypeFromId(otherWidgetId);
        const otherWidget = this.widgets[otherWidgetId];

        const isOtherBasicShape = otherWidgetType === WidgetType.BasicShape;
        const isOtherImage = otherWidgetType === WidgetType.Image;

        const isLayoutOrImage = this.isLayoutWidget(otherWidgetId) || isOtherImage;
        if (!isLayoutOrImage) continue;

        // if its transparent skip it - most likely a border
        if (isOtherBasicShape && isTransparent((otherWidget as BasicShapeWidgetData)?.fillColor[0])) continue;

        const otherWidgetRect = getWidgetRect(otherWidget);
        const isIntersected = intersectRect(otherWidgetRect, widgetRect);

        // it is intersected by another widget we can stop checking
        if (isIntersected) {
          // if its text it has to be covered by at least 30% of the other widget
          if (isWidgetWithText) {
            const isCovered = isWidgetContained(otherWidgetRect, widgetRect, 30);
            if (!isCovered) continue;
          }

          // if its an image behind the widget - it should cover at least 75% of the widget
          if (isOtherImage && isWidgetContained(otherWidgetRect, widgetRect, 75)) {
            const imageWidgetData = this.widgets[otherWidgetId] as ImageWidgetData;

            // if the image has an overlay we use that as the background color
            if (hasOverlay(imageWidgetData) && getOverlayTransparency(imageWidgetData) > this.ALPHA_THRESHOLD) {
              backgroundColor = this.widgetColors[otherWidgetId];
              backgroundInitialColor = getInitialColor(this.initialWidgetColors[otherWidgetId]);
              break;
            }

            // if the image is transparent we skip it
            if (imageWidgetData.opacity <= 0.5) continue;

            // else consider image widgets as no background color
            backgroundColor = '';
            break;
          }

          if (isOtherImage) continue;

          const newWrappedColor = this.widgetColors[otherWidgetId];
          const initialWrappedColor = getInitialColor(this.initialWidgetColors[otherWidgetId]);
          const initialAlpha = getAlpha(initialWrappedColor);

          // if its transparent skip it
          if (initialAlpha < this.ALPHA_THRESHOLD) continue;
          // add it to the alpha color array - we need to blend it with the background color(s)
          if (isWidgetWithText && initialAlpha < 0.8) {
            // should overlap by at least 80% to be considered
            if (!isWidgetContained(otherWidgetRect, widgetRect, 80)) continue;
            // add back the alpha to the new color
            alphaColorArr.push(maintainAlpha(initialWrappedColor, newWrappedColor));
            // continue since we need to find the background color
            continue;
          }

          if (typeof initialColor === 'string' && initialWrappedColor === initialColor && !isTextWidget) {
            sameAsRectColor = newWrappedColor;
            break;
          }

          backgroundColor = newWrappedColor;
          backgroundInitialColor = initialWrappedColor;
          break;
        }
      }

      // blend the all the alpha background colors behind the widget with the background color
      if (alphaColorArr.length) {
        alphaColorArr.push(backgroundColor);
        alphaColorArr.reverse();
        backgroundColor = blendMultipleRgbaColors(alphaColorArr);
      }

      const isOnTopOfBackground = backgroundColor === newPageColor;

      // set the border color to basic shapes
      if (isBasicShapeWithBorder) {
        const initialBorderColor = this.initialWidgetColors[widgetId][1];

        // skip neutral border colors
        const shouldSkip = shouldSkipBorderColor(initialBorderColor, backgroundColor);
        if (shouldSkip) {
          continue;
        }

        let newBorderColor = '';

        // use a color from the theme
        const borderColorMapColor = this.colorMap['border-' + initialBorderColor];
        const colorMapColor = this.colorMap[initialBorderColor];

        if (borderColorMapColor && borderColorMapColor !== backgroundColor) {
          newBorderColor = borderColorMapColor;
        } else if (colorMapColor && colorMapColor !== backgroundColor) {
          newBorderColor = this.colorMap[initialBorderColor];
        } else {
          const themeFiltered = this.theme.filter((color) => color !== backgroundColor);
          newBorderColor = randomPickThemeColor(themeFiltered, this.colorMap, seed);
        }

        this.colorMap['border-' + initialBorderColor] = newBorderColor;
        this.borderColors[widgetId] = newBorderColor;

        if (!this.colorMap[newBorderColor]) this.colorMap[initialBorderColor] = newBorderColor;

        if (!this.widgetColors[widgetId]) {
          this.widgetColors[widgetId] = initialColor[0];
        }

        continue;
      }

      if (isTableWidget) {
        // We need table cell colors for all the existing cell colors
        const widgetTableColors: TableColor = {};
        // for generateDataColors - so we don't generate the same cell color twice
        const preAssignedColors: ColorMap = {};

        // keep initially neutral colors as neutral for the table
        for (const initialCellColor of initialColor as string[]) {
          // keep transparent as transparent
          if (isTransparent(initialCellColor)) {
            preAssignedColors[initialCellColor] = initialCellColor;
            const transparentTextColor = getContrastTextColor(backgroundColor, '', '', this.MIN_TEXT_COLOR_CONTRAST);
            widgetTableColors[initialCellColor] = { cellColor: initialCellColor, textColor: transparentTextColor };
            continue;
          }

          // keep neutral colors as neutral
          if (isNeutral(initialCellColor)) {
            preAssignedColors[initialCellColor] = initialCellColor;
            const textColor = getContrastTextColor(initialCellColor, '', '', this.MIN_TEXT_COLOR_CONTRAST);
            widgetTableColors[initialCellColor] = { cellColor: initialCellColor, textColor };
            continue;
          }
        }

        // add new colors for the table cells
        const cellColors = GET_WIDGET_COLOR_FN_MAP[widgetType](this.widgets[widgetId] as AllWidgetData);
        const { dataColorMap } = generateDataColors(
          cellColors as string[],
          this.theme,
          this.extraThemeColors,
          this.colorMap,
          backgroundColor,
          preAssignedColors,
        );

        // Get contrasting text colors for the new data colors
        for (const [initialCellColor, cellColor] of Object.entries(dataColorMap)) {
          if (widgetTableColors[initialCellColor]) continue;
          const textColor = getContrastTextColor(cellColor, '', '', this.MIN_TEXT_COLOR_CONTRAST);
          widgetTableColors[initialCellColor] = { cellColor, textColor };
        }

        this.tableColors[widgetId] = widgetTableColors;
        // add a color to widgetColors (won't be used for the table)
        this.widgetColors[widgetId] = backgroundColor;
        continue;
      }

      if (isWidgetWithText) {
        const isInitialColorString = typeof initialColor === 'string';

        const textColor = getContrastTextColor(
          backgroundColor,
          // for data text widgets we don't use the initial text color so it will always be black or white
          isInitialColorString ? initialColor : '',
          isInitialColorString ? this.colorMap[initialColor] || this.colorMap['text-widget-' + initialColor] : '',
          this.MIN_TEXT_COLOR_CONTRAST,
        );

        // assign the new text color
        this.widgetColors[widgetId] = textColor;

        // add the text color to the color map if it doesn't exist - so that text widgets will have the same color if they have the same initial color
        if (isInitialColorString && !this.colorMap[initialColor]) {
          this.colorMap['text-widget-' + initialColor] = textColor;
        }

        // if the widget with text doesn't have data colors we don't need to generate them
        if (!isHighchartChartWidget && !isProgressChartWidget && !isStatchartGridIcon) continue;

        let initialDataColors: string[] = [];
        // for scatter bubble charts we only have one color until multi series is implemented
        if (isHighchartChartWidget && isScatterBubbleChartWidget) {
          if (!initialColor) initialDataColors = [this.initialWidgetColors[widgetId] as string];
        } else if (isHighchartChartWidget) {
          initialDataColors = initialColor as string[];
        }

        // for progress chart and statchart grid icon we only want one new data color
        else if (isProgressChartWidget || isStatchartGridIcon) initialDataColors = [initialColor[0] as string];

        const { newDataColors } = generateDataColors(
          initialDataColors,
          this.theme,
          this.extraThemeColors,
          this.colorMap,
          backgroundColor,
        );

        // for statcharts generate a secondary color based off the first color if its not neutral already (grayish)
        this.dataColors[widgetId] = newDataColors;

        // for statcharts generate a secondary color based off the first color if its not neutral already (grayish)
        if ((isProgressChartWidget || isStatchartGridIcon) && !isNeutral(initialColor[1] as string)) {
          const secondaryColor = generateComplimentaryLightColor(newDataColors[0] as string);
          newDataColors.push(secondaryColor);
        }
      } else {
        // check if a color has been assigned to this color already
        if (sameAsRectColor) {
          this.widgetColors[widgetId] = sameAsRectColor;
          continue;
        }

        // keep it the same as the background if its on top of the background and the same color
        if (
          isOnTopOfBackground &&
          isSameAsPageBackground(initialPageColor, widgetType, this.widgets[widgetId] as AllWidgetData)
        ) {
          this.widgetColors[widgetId] = newPageColor;
          continue;
        }

        if (widgetType === WidgetType.Line) {
          // if its a neutral color and its not the same as the background color - keep it the same
          if (isNeutral(initialColor as string) && initialColor !== backgroundColor) {
            this.widgetColors[widgetId] = initialColor as string;
            continue;
          }
        }

        let initialColorString = getInitialColor(initialColor);
        if (initialColorString) {
          // use a color from the theme
          let newColor = this.colorMap[initialColorString];
          const key = backgroundColor + '-' + initialColorString;
          const newColorNested = this.colorMap[key];

          if (!isOnTopOfBackground && newColorNested) newColor = newColorNested;

          const isSameAsBackground = newColor === backgroundColor && backgroundInitialColor !== initialColorString;
          if (newColor && !isSameAsBackground) {
            this.widgetColors[widgetId] = newColor;

            if (!this.colorMap[initialColorString] && initialColor && typeof initialColorString === 'string')
              this.colorMap[initialColorString] = newColor;
            if (!this.colorMap[key] && !isOnTopOfBackground) this.colorMap[key] = newColor;

            continue;
          }
        }

        const themeFiltered = this.theme.filter((color) => color !== backgroundColor);
        let newColor = randomPickThemeColor(themeFiltered, this.colorMap, seed);
        this.widgetColors[widgetId] = newColor;

        if (typeof initialColorString === 'string') {
          if (initialColor && !this.colorMap[initialColorString]) this.colorMap[initialColorString] = newColor;
          const key = backgroundColor + '-' + initialColorString;
          if (!this.colorMap[key] && !isOnTopOfBackground) this.colorMap[key] = newColor;
        }
      }
    }
  };

  /**
   * Shuffles the colors of the widgets on the infograph based on the theme colors
   *
   * @param initialTheme
   * @param initialInfograph
   * @param selectedPageId -> apply shuffle to a specific page
   * @param colorMapConfig -> color map config
   * @param seed -> random seed
   *
   * @returns An array of updated widgets and the new background colors
   */
  public shuffle = (
    initialTheme: string[],
    initialInfograph: InfographState,
    selectedPageId?: PageId[],
    colorMapConfig?: ColorMapConfig,
    seed?: number,
  ) => {
    // clone it so we don't mutate the original
    const infograph = clonedeep(initialInfograph);
    const presetColorMapConfig = colorMapConfig ? clonedeep(colorMapConfig) : undefined;

    this.widgets = infograph.widgets;
    this.colorMap = presetColorMapConfig?.colorMap || {};
    this.pageColorMap = presetColorMapConfig?.pageColorMap || {};
    this.widgetColors = {};
    this.dataColors = {};
    this.tableColors = {};
    this.updateWidgets = [];
    this.updatePageColors = {};
    this.borderColors = {};

    setInitialWidgetColors(this.widgets, this.initialWidgetColors);
    setInitialPageColors(infograph, this.initialPageColors);

    // normalize the theme to hex so that we can consistently compare colors
    const initialThemeHex = initialTheme.map((c) => toHex(c));
    // get the background from the theme before shuffling
    this.theme = presetColorMapConfig?.theme || shuffleThemeColors(initialThemeHex, this.previousTheme, seed);
    this.extraThemeColors =
      presetColorMapConfig?.extraThemeColors ||
      shuffleThemeColors(generatePaletteFromColor(randomPickThemeColor(this.theme, {}, seed)), [])
        .map((c) => toHex(c))
        // exclude colors from the theme
        .filter((color) => !this.theme.includes(color));

    // keep track of the theme for the next shuffle
    // if a config is passed in we don't need to keep the previous theme
    if (!presetColorMapConfig?.theme) this.previousTheme = this.theme;

    const pageOrder = infograph.pageOrder;
    let isFirstPageShuffle = true;
    for (const pageId of pageOrder) {
      if (selectedPageId && selectedPageId.length && !selectedPageId.includes(pageId)) continue;

      const page = infograph.pages[pageId];
      const initialPageColor = this.initialPageColors[pageId];
      const currentPageColor = page.gradientBackground ? 'gradient' : page.background;

      // the new page color
      // used to make sure widgets on top are not the same color
      let newPageColor = '';

      // shuffle the gradient background
      if (page.gradientBackground) {
        const newGradient = replaceGradientColors(page.gradientBackground, this.theme);
        this.pageGradientColorMap[pageId] = newGradient;
        this.updatePageColors[pageId] = newGradient;

        // we need a solid color to check that widgets on top are not the same color
        // set it to the first color in the gradient
        newPageColor = newGradient.colors[0];
      } else {
        // shuffle the solid color background
        newPageColor = getBackgroundColor(
          initialThemeHex,
          initialPageColor,
          currentPageColor,
          this.BACKGROUND_COLOR_LUMINANCE,
          this.pageColorMap,
          seed,
        );
        this.updatePageColors[pageId] = newPageColor;
      }

      // add it to the page color map & update page colors
      if (!this.pageColorMap[currentPageColor]) this.pageColorMap[currentPageColor] = newPageColor;

      // flatten groups and statcharts (we need to look at all the group  members but not the group itself)
      const flattenedLayerOrder = getFlattenedLayerOrder(page, this.widgets, true);

      // find the decorative widgets first - if a layout widgets without text / icons on top of it
      const decorativeLayoutWidgets = this.getDecorativeLayoutWidgets(flattenedLayerOrder);
      const decorativeLayoutWidgetMap: DecorativeLayoutWidgetMap = {};
      decorativeLayoutWidgets.forEach((shape) => {
        decorativeLayoutWidgetMap[shape.id] = shape;
      });

      // Find the base widget layout widgets (directly on top of the background) and not-decorative
      // These will be the first to get colors from the theme and determine the color for widgets on top of them
      const baseLayoutWidgets = this.getPageBaseLayoutWidgets(flattenedLayerOrder).filter(
        (shape) => !decorativeLayoutWidgetMap[shape.id],
      );

      // Find all the layout widgets that are fully covered (usually cannot be the same color as the widget that covers them)
      const fullyCoveredLayoutWidgets = this.getPageFullyCoveredLayoutWidgets(flattenedLayerOrder).filter(
        (shape) => !decorativeLayoutWidgetMap[shape.id],
      );

      // partially intersected / covered by n other layout widgets (and possible the background)
      const partiallyCoveredLayoutWidgets = this.getPagePartiallyCoveredLayoutWidgets(
        flattenedLayerOrder,
        decorativeLayoutWidgetMap,
      ).filter((shape) => !decorativeLayoutWidgetMap[shape.id]);

      // Object with [widgetId] - shape to make it easier to look up when setting colors
      const layoutMap = createLayoutMap(
        decorativeLayoutWidgets,
        baseLayoutWidgets,
        fullyCoveredLayoutWidgets,
        partiallyCoveredLayoutWidgets,
      );

      // 1. start with applying colors to the base widgets
      this.setPageBaseWidgetColors(
        baseLayoutWidgets,
        initialPageColor,
        newPageColor,
        !isFirstPageShuffle || !!colorMapConfig,
      );

      // 2. Set colors for the base widgets on top of the base widgets
      this.setPageLayeredColors(
        flattenedLayerOrder,
        fullyCoveredLayoutWidgets,
        partiallyCoveredLayoutWidgets,
        layoutMap,
        newPageColor,
        seed,
      );

      // 3. Set the colors of the remaining widgets
      this.setRemainingPageWidgetColors(
        flattenedLayerOrder,
        initialPageColor,
        page.widgetLayerOrder,
        newPageColor,
        seed,
      );

      // Add the updated widgets to the updateWidgets array
      for (const widgetId in this.widgetColors) {
        const widgetType = getWidgetTypeFromId(widgetId);

        const setWidgetColor = SET_WIDGET_COLOR_FN_MAP[widgetType];
        if (!setWidgetColor) continue;
        if (this.updateWidgets.find((widget) => widget.widgetId === widgetId)) continue;

        const initialColor = this.initialWidgetColors[widgetId];

        // Maintain alpha for single color widgets
        const newColor = Array.isArray(initialColor)
          ? this.widgetColors[widgetId]
          : maintainAlpha(initialColor, this.widgetColors[widgetId]);

        const newDataColors = this.dataColors[widgetId] || [];
        const newTableColors = this.tableColors[widgetId] || {};
        const newBorderColor = this.borderColors[widgetId] || '';

        const updatedWidget = {
          widgetId: widgetId,
          widgetData: setWidgetColor(
            this.widgets[widgetId] as AllWidgetData,
            newColor,
            newDataColors,
            newTableColors,
            newBorderColor,
          ),
        };

        this.updateWidgets.push(updatedWidget);
      }

      isFirstPageShuffle = false;
    }

    const appliedColorMap = {
      // For applying the same colors to the next shuffle
      colorMap: clonedeep(this.colorMap),
      pageColorMap: clonedeep(this.pageColorMap),
      theme: clonedeep(this.theme),
      extraThemeColors: clonedeep(this.extraThemeColors),
    };

    return { updatePageColors: this.updatePageColors, updateWidgets: this.updateWidgets, appliedColorMap };
  };

  /**
   * Resets the stored initial background color (used for the color shuffle logic)
   * (will be reset once shuffle runs again)
   */
  public resetBackground = (pageId: PageId) => {
    if (this.initialPageColors[pageId]) delete this.initialPageColors[pageId];
  };
}
